<template>
  <VDialogV3
    ref="dialog"
    class="demand-option-dialog"
    position="right"
    width="600px"
    :entity="localOption"
    :context-menu="DemandsV2Service.demandOptionCardContextMenu"
    :tabs="tabs"
    v-model:active-tab="activeTab"
    @close="closeDialog"
  >
    <DemandOptionPrimaryTab
      v-if="activeTab.id === 'primary'"
      :option="localOption"
    />

    <!-- v-else-if, а не отдельный v-if: две независимые ветки компилируются
         в два блока со своими якорями, и при переключении таба Vue гасит одну
         и вставляет другую относительно уже уехавшего якоря. Ветки и так
         взаимоисключающие — различаются по activeTab.id. -->
    <DemandOptionSecondaryTab
      v-else-if="activeTab.id === 'secondary'"
      :option="localOption"
    />

    <template #footer>
      <VButton
        theme="primary"
        :disabled="isLoading"
        @click="submit"
      >
        {{ submitButtonLabel }}
      </VButton>

      <template v-if="createMode">
        <VButton
          theme="default"
          :disabled="isLoading"
          @click="saveAndClear"
        >
          Создать и очистить
        </VButton>

        <VLink
          material="info"
          @mouseenter="$core.tooltip.show($event.currentTarget, saveAndClearHint)"
          @mouseleave="$core.tooltip.hide()"
        />
      </template>

      <VButton @click="closeDialog">
        Отмена
      </VButton>
    </template>
  </VDialogV3>
</template>

<script lang="ts">

import Core                     from '@/core/Core';
import DemandOption             from '@/domains/DemandsV2/DemandOption';
import DemandsV2Service         from '@/domains/DemandsV2/DemandsV2Service';

import DemandOptionPrimaryTab   from '@/domains/DemandsV2/components/DemandV2Card/DemandV2OptionDialog/DemandOptionPrimaryTab.vue';
import DemandOptionSecondaryTab from '@/domains/DemandsV2/components/DemandV2Card/DemandV2OptionDialog/DemandOptionSecondaryTab.vue';

export default defineComponent({
  name: 'DemandV2OptionDialog',

  components: {
    DemandOptionPrimaryTab,
    DemandOptionSecondaryTab,
  },

  props: {
    demandOption: {
      type:     DemandOption,
      required: true,
    },
  },

  setup() {
    return {
      DemandsV2Service,
    };
  },

  data() {
    const tabs = [
      { id: 'primary',   name: 'Основное' },
      { id: 'secondary', name: 'Другое' },
    ];

    return {
      localOption: Core.utils.cloneDeep(this.demandOption) as DemandOption,

      tabs,
      /** Объект таба, а не его id: VDialogV3 принимает и возвращает через
       *  v-model:active-tab именно элемент списка tabs. Раньше здесь лежала
       *  строка, но связь была разорвана опечаткой в биндинге (:v-model:...),
       *  поэтому расхождение не проявлялось — а вместе с ним не работало и
       *  переключение вкладок. */
      activeTab: tabs[0],

      popoverVisible: false,
      isLoading: false,

      /** Что-то сохранили за время жизни диалога (в том числе через «Создать и очистить») */
      isChanged: false,
    };
  },

  computed: {
    editMode(): boolean {
      return Boolean(this.localOption.isExisting);
    },

    createMode(): boolean {
      return this.editMode === false;
    },

    submitButtonLabel(): string {
      if (this.editMode) {
        return 'Сохранить';
      }
      else {
        return 'Создать';
      }
    },

    saveAndClearHint(): string {
      return '"Создать и очистить" означает создание предложения с сохранением '
        + 'заполненных полей поставщика, юр. лица и производителя и очисткой всех остальных.'
        + '\n\nКомбинация клавиш: Ctrl + Enter';
    },
  },

  created() {
    window.addEventListener('keydown', this.handleKeyDown);
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
  },

  methods: {
    async submit(): Promise<void> {
      const isSaved = await this.saveOption();

      if (isSaved === true) {
        this.$core.dialogs.DemandV2OptionDialog.resolve();
      }
    },

    async saveAndClear(): Promise<void> {
      const isSaved = await this.saveOption();

      if (isSaved === true) {
        this.createNewOptionFromCurrent();
        this.resetValidationState();
      }
    },

    async saveOption(): Promise<boolean> {
      const isValid = await (this.$refs.dialog as any).validate();

      if (isValid === false) {
        return false;
      }

      this.isLoading = true;
      let isSuccess: boolean;

      if (this.editMode) {
        isSuccess = await DemandsV2Service.updateOption(this.localOption);
      }
      else {
        isSuccess = await DemandsV2Service.addOption(this.localOption);
      }

      if (isSuccess === true) {
        this.isChanged = true;
      }

      this.isLoading = false;

      return isSuccess;
    },

    createNewOptionFromCurrent(): void {
      this.localOption = DemandOption.fromAnotherDemandOption(this.localOption);
    },

    /** todo этот метод унести в VForm компонент */
    resetValidationState(): void {
      const form = this.$refs.dialog.formRef as any;

      for (const [, control] of form.controls) {
        if (control && control.validationReset) {
          control.validationReset();
        }
      }
    },

    handleKeyDown(event: KeyboardEvent): void {
      if (event.ctrlKey && event.key === 'Enter') {
        this.saveAndClear();
      }
    },

    closeDialog(): void {
      if (this.isChanged === true) {
        this.$core.dialogs.DemandV2OptionDialog.resolve();
      }
      else {
        this.$core.dialogs.DemandV2OptionDialog.hide();
      }
    },
  },
});
</script>